import {ChevronRightIcon} from '@heroicons/react/outline';
import {isEmpty} from 'lodash-es';
import {useState} from 'react';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';
import {hasAdminPermission} from '@afriex/core/utils';

import {Button, InfoBox} from '@/components';
import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import ConfirmationModal from '@/components/Modals/ConfirmationModal';
import {useAppRates} from '@/context/AppRatesContext';
import AppRatesTable from '@/screens/Rates/App/RatesTable';
import useOTCTradeUpdate from '@/screens/Rates/OTCTrades/useOTCTradeUpdate';
import type {AfriexOTCTrade, RatesMap, UserType} from '@/types';

import ArbitrageDisplay from './ArbitrageDisplay';
import RatesUploadForm from './RatesUploadForm';
import UpdateTradeModal from './UpdateTradeModal';
import useRatesUpdate from './useRatesUpdate';

type RatesListProps = {
  target?: UserType;
  /** Test hook prefix; the form, actions, preview and dialogs namespace under it. */
  dataCy?: string;
};
const RatesUpload = ({target, dataCy}: RatesListProps) => {
  const {state} = useAppRates();
  const {
    arbitrageResult,
    handleTemplateDownload,
    handleRatesUpload,
    appRates: newAppRates,
    tolerance,
    handleToleranceChange,
    isArbitrageChecked,
    isArbitrageDetected,
    handleRatesCancellation,
    handleRatesUpdate,
    isLoading,
    isConfirmModalOpen,
    setIsConfirmModalOpen,
    loadingText,
    // handleOTCRatesUpload,
    otcRates: newOTCRates,
  } = useRatesUpdate(target);

  const [isTradeVerificationOpen, setIsTradeVerificationOpen] = useState(false);
  const [isOTCTradeModalOpen, setIsOTCTradeModalOpen] = useState(false);
  const [newOTCTrades, setNewOTCTrades] = useState<AfriexOTCTrade[]>();

  // Declared after the `useState` calls it closes over. Hook call order is
  // unchanged from render to render, and the callback body is identical.
  const {handleSubmit} = useOTCTradeUpdate(() => setIsOTCTradeModalOpen(false));

  const showContinueBtn =
    isArbitrageChecked &&
    !isArbitrageDetected &&
    !isEmpty(newAppRates) &&
    !isEmpty(newOTCRates);

  // Recording the trade behind these rates writes OTC volume, which only a
  // role holding OTCVolumeManage may do, so the others skip that question.
  const canRecordTrade = hasAdminPermission(
    localStorage.getItem('admin-role') ?? undefined,
    AfriexAdminPermission.OTCVolumeManage,
  );

  const appRates = {old: state.appRates, new: newAppRates as RatesMap};
  const otcRates = {old: state.otcVolume, new: newOTCRates};

  async function submitUpload() {
    await handleRatesUpdate(newAppRates, newOTCRates);
    if (newOTCTrades) {
      await handleSubmit(newOTCTrades);
    }
  }

  return (
    <div className="p-4 md:p-0">
      <div className="flex justify-center">
        <PermissionsProvider permission={AfriexAdminPermission.RatesUpload}>
          <RatesUploadForm
            handleRatesUpload={handleRatesUpload}
            handleTemplateDownload={handleTemplateDownload}
            dataCy={dataCy ? `${dataCy}-form` : undefined}
          />
        </PermissionsProvider>
      </div>
      <br />

      {loadingText && (
        <InfoBox>
          <svg
            className="animate-spin h-5 w-5 mr-3 ..."
            viewBox="0 0 24 24"
          ></svg>
          {loadingText}
        </InfoBox>
      )}

      <PermissionsProvider permission={AfriexAdminPermission.RatesUpload}>
        <section className="flex justify-center mb-3">
          {/*
            The button is gated entirely on showContinueBtn: it is hidden until
            the upload is ready rather than shown in a disabled state. It used
            to also carry disabled={isDisabled}, where isDisabled was
            `!isArbitrageChecked || isArbitrageDetected || isEmpty(newAppRates)`,
            every disjunct of which showContinueBtn already requires to be
            false. The prop could therefore never be true while the button was
            rendered.
          */}
          {showContinueBtn && (
            <Button
              onClick={() =>
                canRecordTrade
                  ? setIsTradeVerificationOpen(!isTradeVerificationOpen)
                  : setIsConfirmModalOpen(true)
              }
              variant="solid"
              colorScheme="green"
              rightIcon={<ChevronRightIcon className="w-4 h-4" />}
              dataCy={dataCy ? `${dataCy}-continue` : undefined}
            >
              Continue
            </Button>
          )}
        </section>
      </PermissionsProvider>

      {arbitrageResult && isArbitrageChecked && (
        <ArbitrageDisplay
          isOpen={arbitrageResult && isArbitrageChecked}
          arbitrageResult={arbitrageResult}
          rates={appRates.new}
          tolerance={tolerance}
          onToleranceChange={handleToleranceChange}
        />
      )}

      {!isEmpty(newAppRates) && !isArbitrageDetected && (
        <Button
          onClick={handleRatesCancellation}
          variant="solid"
          dataCy={dataCy ? `${dataCy}-clear` : undefined}
        >
          Clear
        </Button>
      )}

      {/* Rates Table */}
      {!isEmpty(newAppRates) && (
        <div className="table-con overflow-x-auto">
          <h3>App Rates</h3>
          <AppRatesTable
            rates={appRates.new}
            dataCy={dataCy ? `${dataCy}-preview` : undefined}
          />
        </div>
      )}

      <UpdateTradeModal
        isOpen={isOTCTradeModalOpen}
        title="Enter Trade"
        onClose={() => setIsOTCTradeModalOpen(false)}
        appRates={appRates}
        otcRates={otcRates}
        proceedFn={(newTrades) => {
          setNewOTCTrades(newTrades);
          setIsConfirmModalOpen(true);
        }}
      />

      <ConfirmationModal
        data-tip
        data-for="tradeVerification"
        isOpen={isTradeVerificationOpen}
        label=""
        cancelAction={() => {
          setIsTradeVerificationOpen(false);
          setIsConfirmModalOpen(true);
        }}
        cancelActionLabel="No"
        confirmAction={() => {
          setIsTradeVerificationOpen(false);
          setIsOTCTradeModalOpen(true);
        }}
        confirmActionLabel="Yes"
        loadingLabel={loadingText}
        loading={isLoading}
        title="Did you make Trade?"
        dataCy={dataCy ? `${dataCy}-trade-question` : undefined}
      />

      <ConfirmationModal
        data-tip
        data-for="productionPush"
        isOpen={isConfirmModalOpen}
        label="Are you sure you want to save these rates?"
        cancelAction={() => setIsConfirmModalOpen(false)}
        cancelActionLabel="No"
        confirmAction={submitUpload}
        confirmActionLabel="Yes"
        loadingLabel={loadingText}
        loading={isLoading}
        title="Confirm to submit"
        dataCy={dataCy ? `${dataCy}-save` : undefined}
      />
    </div>
  );
};

export default RatesUpload;
